/**
 * One order, in full — `GET /v1/consumer/orders/{id}` and the actions available on it.
 *
 * **The server decides everything a person is allowed to do.** `can_cancel`,
 * `cancellation_charge_now` and `queue_position` are read, not computed. The client's own
 * `canCancel` in `model/tracking.ts` exists only to avoid rendering a button that is certain to
 * fail; where the two disagree, this field wins.
 *
 * Every money and volume figure is a decimal string and stays one. The client formats.
 */
import type { StartedSbpPayment } from './sbp';
import { apiRequest } from '@/shared/api/client';

/** Бензовоз, который приедет. `display` собран сервером — «Лада Ларгус · А123БВ777», — чтобы
 *  сайт и оба приложения не склеивали одну и ту же строку каждый по-своему; отдельные поля рядом
 *  потому, что копируют их по одному. Марка бывает не заведена: тогда `display` — это один номер. */
export interface OrderTruckView {
  id: string;
  plate_number: string;
  brand: string | null;
  model: string | null;
  display: string;
}

/**
 * Фискальный чек заказа — настоящий, от кассы.
 *
 * Сервер отдаёт только выпущенные: чек, который касса ещё не провела или не провела вовсе, клиенту
 * говорить нечего. Поэтому пустой список — обычное состояние заказа, который ещё не оплачен, а не
 * признак поломки.
 */
export interface OrderReceipt {
  id: string;
  /** `completion` — заправка, `sbp_prepayment` — аванс (`sbp_prepayment_*` — аванс машины,
   *  добавленной в выезд по СБП), `sbp_refund_*` — возврат его части, `paid_cancellation` /
   *  `no_show` — удержание. */
  trigger: string;
  amount: string;
  /** Номер фискального документа. */
  fiscal_document_number: string | null;
  /** Страница чека у ОФД. `null`, когда ОФД её не дал: собрать ссылку самим не из чего. */
  receipt_url: string | null;
  issued_at: string | null;
}

export interface OrderCarView {
  id: string;
  car_id: string;
  car_brand: string;
  car_model: string;
  car_plate: string;
  car_color: string;
  status: string;
  requested_liters: string;
  /** Present once this car has actually been filled. */
  poured_liters: string | null;
  held_amount: string;
  fuel_hold_amount: string;
}

export interface OrderHistoryEntry {
  state: string;
  actor_type: string;
  actor_id: string | null;
  occurred_at: string;
  metadata: Record<string, unknown> | null;
}

export interface OrderDetail {
  id: string;
  state: string;
  /** What the tracking screen classifies on. */
  tracking_substate: string;

  address_text: string;
  latitude: number;
  longitude: number;
  operator_comment: string;
  owner_present: boolean;

  car_id: string;
  car_brand: string;
  car_model: string;
  car_plate: string;
  car_color: string;
  cars: OrderCarView[];
  /** Чеки, пробитые по этому заказу, от старого к новому. */
  receipts: OrderReceipt[];

  delivery_speed: string;
  desired_slot_label: string | null;
  scheduled_from: string | null;
  scheduled_to: string | null;

  operator_assigned: boolean;
  operator_name: string | null;
  /** Машина, которая едет: марка, модель и госномер. Появляется вместе с назначением; `null`,
   *  пока заказ никому не отдан. Экран слежения показывает ЕЁ, а не имя заправщика — во дворе
   *  человек ищет машину (решение владельца 2026-08-28). */
  refuel_car: OrderTruckView | null;
  /** Place in the dispatch queue while still unassigned. Server-computed. */
  queue_position: number | null;

  /** Server's verdict on whether cancelling is possible right now. */
  can_cancel: boolean;
  /** What cancelling would cost **at this moment** — not the same as the fee's nominal amount. */
  cancellation_charge_now: string;
  cancellation_fee_amount: string;
  cancellation_fee_text: string;
  cancellation_fee_charged_amount: string;
  cancellation_fee_refunded_amount: string;
  charged_on_cancellation: boolean;

  held_amount: string;
  delivery_amount: string | null;
  urgency_amount: string;
  /* Сколько сняла персональная скидка с этого заказа. Все суммы рядом УЖЕ со скидкой, поэтому
     вычитать это число из них ещё раз нельзя — оно идёт подписью под «Итого», вместе с
     `total_price_before_discount`. `?` потому что бэкенд постарше полей не пришлёт. */
  discount_amount?: string;
  /* Итого ДО скидки — то, что на чеке зачёркивают. */
  total_price_before_discount?: string | null;
  fuel_total_amount: string | null;
  fuel_amount_liters: string | null;
  /**
   * The price per litre this order was calculated at — frozen at quote time, not today's city
   * price.
   *
   * Sent so the receipt can write «32,5 л × 58,90 ₽» instead of dividing one total by the other:
   * on an order that was billed up to the city minimum, the litres charged and the litres poured
   * are different numbers, and the quotient would be a price that never existed in any price list.
   * `null` until the order is priced — the same moment `total_price` becomes known.
   */
  fuel_unit_price: string | null;
  requested_fuel_liters: string | null;
  billed_liters: string | null;
  total_price: string | null;
  captured_from_hold: string | null;
  refunded_from_hold: string | null;
  min_order_applied: boolean;

  /** When an unattended order will be auto-cancelled for lack of access. */
  no_show_deadline_at: string | null;

  rating_score: number | null;
  rating_comment: string | null;

  /**
   * Карта, с которой платили. `null` у заказа по СБП — сохранённой карты там нет вовсе.
   *
   * Отсюда и правило для повтора: заказ без карты начинается с шага оплаты, а не прыгает на
   * сводку с пустым способом оплаты.
   */
  payment_method_id: string | null;
  /**
   * Чем оплачен заказ: `card` | `sbp`. По нему экран выбирает, как добавить машину в выезд: к
   * заказу по карте — картой, к заказу по СБП — отдельным QR. `undefined` — сервер постарше поля
   * не шлёт; тогда о СБП говорит пустой `payment_method_id`.
   */
  payment_kind?: 'card' | 'sbp';
  created_at: string;
}

/** Заказ оплачен по СБП — к нему машина добавляется отдельным QR, а не картой. */
export function isSbpOrder(
  order: Pick<OrderDetail, 'payment_kind' | 'payment_method_id'>,
): boolean {
  if (order.payment_kind !== undefined) return order.payment_kind === 'sbp';
  return order.payment_method_id === null;
}

export interface OrderDetailResponse {
  order: OrderDetail;
  history: OrderHistoryEntry[];
}

export function fetchOrderDetail(id: string, signal?: AbortSignal): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/v1/consumer/orders/${id}`, {
    ...(signal ? { signal } : {}),
  });
}

export interface CancelReason {
  /** Stable machine code stored on the order and in the audit trail — never localized. */
  code: string;
  label: string;
  /** The «Другое» case: the server rejects the cancellation without a non-blank `reason_text`. */
  requires_text: boolean;
}

/** The reasons are the server's, not a list this app keeps — they change without a release. */
export function fetchCancelReasons(): Promise<CancelReason[]> {
  return apiRequest<CancelReason[]>('/v1/consumer/orders/cancel-reasons');
}

/** Answers with the whole order, so the screen can render the outcome without a second round-trip. */
export function cancelOrder(
  id: string,
  reasonCode: string,
  reasonText?: string,
): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/v1/consumer/orders/${id}/cancel`, {
    method: 'POST',
    body: {
      reason_code: reasonCode,
      ...(reasonText !== undefined && reasonText.trim().length > 0
        ? { reason_text: reasonText.trim() }
        : {}),
    },
  });
}

/** Ответ на повторную отправку чеков: куда ушло письмо и сколько в нём чеков. */
export interface ReceiptResendResult {
  sent_to: string;
  receipts: number;
}

/**
 * «Отправить повторно на email» — письмо со ссылками на все выданные чеки заказа в ОФД.
 *
 * Шлёт его сервер, на адрес учётной записи: у кассы повторной отправки нет. Повтор в течение
 * минуты сервер отвечает тем же, но второго письма не ставит, — двойное нажатие безопасно.
 */
export function resendOrderReceipts(orderId: string): Promise<ReceiptResendResult> {
  return apiRequest<ReceiptResendResult>(`/v1/consumer/orders/${orderId}/receipts/resend`, {
    method: 'POST',
  });
}

/** Takes one car off a running выезд. The last pending car cannot be dropped — cancel instead. */
export function dropOrderCar(orderId: string, carId: string): Promise<void> {
  return apiRequest<void>(`/v1/consumer/orders/${orderId}/cars/${carId}`, { method: 'DELETE' });
}

export interface AddedCarQuote {
  estimated_fuel_liters: string;
  fuel_unit_price: string;
  /** What will be held for this car. No delivery line — the выезд is already paid for. */
  hold_amount: string;
}

/**
 * What adding a car to a **running** order would cost.
 *
 * A separate endpoint from the trip quote because the arithmetic differs: delivery was charged
 * once when the order was placed, so this car adds fuel and a hold and nothing else. That absence
 * is the whole point of the separate shape — a client reusing the trip quote here would show a
 * second delivery fee that will not be charged.
 */
export function quoteAddedCar(
  orderId: string,
  carId: string,
  estimatedFuelLiters: string,
): Promise<AddedCarQuote> {
  return apiRequest<AddedCarQuote>(`/v1/consumer/orders/${orderId}/cars/quote`, {
    method: 'POST',
    body: { car_id: carId, estimated_fuel_liters: estimatedFuelLiters },
  });
}

export function addOrderCar(
  orderId: string,
  carId: string,
  estimatedFuelLiters: string,
): Promise<void> {
  return apiRequest<void>(`/v1/consumer/orders/${orderId}/cars`, {
    method: 'POST',
    body: { car_id: carId, estimated_fuel_liters: estimatedFuelLiters },
  });
}

/**
 * Дозаказ машины в выезд по СБП: QR на топливо этой машины, без второй доставки (12.10.2026).
 *
 * У заказа по СБП карты нет, и добавить машину «картой» нельзя. Сервер выдаёт отдельный код на
 * сумму топлива — ту же, что назвал `quoteAddedCar`; живой код по той же машине и тем же литрам
 * отдаёт повторно. Дальше — тот же опрос `awaitSbpPayment`: как только банк подтвердит, сервер
 * добавит машину в выезд сам, и `order_id` в статусе платежа укажет на этот заказ.
 */
export function startAddCarSbpPayment(
  orderId: string,
  carId: string,
  estimatedFuelLiters: string,
): Promise<StartedSbpPayment> {
  return apiRequest<StartedSbpPayment>(`/v1/consumer/orders/${orderId}/cars/sbp`, {
    method: 'POST',
    body: { car_id: carId, estimated_fuel_liters: estimatedFuelLiters },
  });
}

/**
 * Rates a finished order.
 *
 * A score of 1 or 2 requires a comment — the backend 400s without one — and opens a support
 * thread on the server side. Recorded once: `rated_at` makes a second attempt a no-op.
 */
export function rateOrder(
  orderId: string,
  score: number,
  comment?: string,
): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/v1/consumer/orders/${orderId}/rating`, {
    method: 'POST',
    body: { score, ...(comment !== undefined && comment.length > 0 ? { comment } : {}) },
  });
}
